// src/pages/ArticlePost.jsx
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { posts } from '../posts/index.js';
import { Container } from '../components/Container.jsx';
import { formatDate } from '../lib/formatDate.js';
import { Prose } from '../components/Prose';
import { Header } from '../components/Header.jsx';
import { Footer } from '../components/Footer.jsx';

// Define postContents with absolute path from src/
const postContents = import.meta.glob('/src/posts/*/content.md', {
    query: '?raw',
    import: 'default',
    eager: true,
});

function ArticlePost() {
    const { slug } = useParams();
    const post = posts.find((p) => p.slug === slug);
    const [content, setContent] = useState('');

    useEffect(() => {
        if (post) {
            const mdPath = `/src/posts/${post.path}`;
            if (postContents[mdPath]) {
                setContent(postContents[mdPath]);
            } else {
                console.error('Markdown file not found:', mdPath);
                console.log('Available paths:', Object.keys(postContents));
            }
        }
    }, [post]);

    if (!post) {
        return <div className="container mx-auto px-4 py-10">Article not found</div>;
    }

    // Custom renderer for images
    const renderers = {
        img: ({ src, alt }) => {
            // Handle relative paths (e.g., ./image.jpg or image.jpg)
            const normalizedSrc = src.startsWith('./') ? src.slice(2) : src;
            const imagePath = `/posts/${post.slug}/${normalizedSrc}`;
            return <img src={imagePath} alt={alt} className="max-w-full h-auto" />;
        },
    };

    return (
        <div className="container mx-auto px-4">
            <Helmet>
                <title>Michael Leitz - {post.title}</title>
                <meta name="description" content={`Article: ${post.title}`} />
            </Helmet>
            <Header />
            <main className="main flex flex-col items-center py-10">
                <Container className="mt-16 lg:mt-32">
                    <div className="xl:relative">
                        <div className="mx-auto max-w-2xl">
                            <article>
                                <header className="flex flex-col">
                                    <h1 className="mt-6 text-4xl font-bold tracking-tight text-zinc-800 dark:text-zinc-100 sm:text-5xl">
                                        {post.title}
                                    </h1>
                                    <time
                                        dateTime={post.date}
                                        className="order-first flex items-center text-base text-zinc-400 dark:text-zinc-500"
                                    >
                                        <span className="h-4 w-0.5 rounded-full bg-zinc-200 dark:bg-zinc-500" />
                                        <span className="ml-3">{formatDate(post.date)}</span>
                                    </time>
                                </header>
                                <Prose className="mt-8" data-mdx-content>
                                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={renderers}>
                                        {content}
                                    </ReactMarkdown>
                                </Prose>
                            </article>
                        </div>
                    </div>
                </Container>
            </main>
            <Footer />
        </div>
    );
}

// ... rest of your code (ArrowLeftIcon, ArticleLayout) ...

export default ArticlePost;
